import React, { useContext, useMemo, useRef } from "react";
import { ApolloClient, InMemoryCache, ApolloProvider, from } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";

import { AuthContext } from "../context/AuthContext";

const ApolloProviderWrapper = (props) => {
    const { context, dispatch, getCurrentJWT, logOut } = useContext(AuthContext);

    const apiUrl = process?.env?.REACT_APP_VPORT_API_URL;

    if (!apiUrl) {
        console.error(
            `Error getting API Url (${process.env.REACT_APP_BUILD_ENV}): `,
            process?.env?.REACT_APP_VPORT_API_URL
        );
    }

    // Keep auth callbacks/user current without recreating the Apollo client every render
    const authRef = useRef({ getCurrentJWT, logOut, dispatch, user: context.user });
    authRef.current = { getCurrentJWT, logOut, dispatch, user: context.user };

    const client = useMemo(() => {
        const httpLink = createUploadLink({
            uri: apiUrl,
        });

        const authMiddleware = setContext(async () => {
            const { getCurrentJWT: getJWT, user } = authRef.current;
            const JWT = await getJWT(user);
            return {
                headers: {
                    Authentication: JWT ? `Bearer ${JWT}` : null,
                },
            };
        });

        const errorLink = onError(({ graphQLErrors, networkError }) => {
            if (graphQLErrors) {
                graphQLErrors.forEach(({ message, locations, path, extensions }) => {
                    console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

                    if (process.env.REACT_APP_BUILD_ENV !== "prod") {
                        console.log(`[GraphQL error]: Extensions: `, extensions);
                    }

                    if (extensions?.code === "UNAUTHENTICATED") {
                        authRef.current.logOut();
                        authRef.current.dispatch({ type: "LOGOUT" });
                    }
                });
            }
            console.log(`[Network error]: ${networkError}`);
            if (networkError) {
                if (networkError === "No current user") {
                    authRef.current.logOut();
                    authRef.current.dispatch({ type: "LOGOUT" });
                    throw networkError;
                }
            }
        });

        return new ApolloClient({
            link: from([errorLink, authMiddleware, httpLink]),
            cache: new InMemoryCache({
                typePolicies: {
                    // Vlink is a singleton namespace under Query — no id field
                    Vlink: {
                        keyFields: false,
                        merge: true,
                    },
                    Query: {
                        fields: {
                            vlink: {
                                merge: true,
                            },
                        },
                    },
                },
            }),
        });
    }, [apiUrl]);

    return <ApolloProvider client={client}>{props.children}</ApolloProvider>;
};

export default ApolloProviderWrapper;
